import { useRichTextEditorContext } from 'mui-tiptap';
import { MenuButton, type MenuButtonProps } from 'mui-tiptap';
import FormatIndentIncreaseIcon from '@mui/icons-material/FormatIndentIncrease';
import FormatIndentDecreaseIcon from '@mui/icons-material/FormatIndentDecrease';

import { ListTypes } from './types';

const getIndentLevel = (
  editor: ReturnType<typeof useRichTextEditorContext>
) => {
  if (!editor) return 0;

  const paragraphIndent = editor.getAttributes('paragraph').indent;
  const headingIndent = editor.getAttributes('heading').indent;

  return paragraphIndent || headingIndent || 0;
};

type IndentButtonProps = Partial<MenuButtonProps> & {
  maxLevel?: number;
};

export const MenuButtonIncreaseIndent = ({
  maxLevel = 8,
  ...props
}: IndentButtonProps) => {
  const editor = useRichTextEditorContext();

  if (!editor) {
    return null;
  }

  const isInList =
    editor.isActive(ListTypes.Bullet) || editor.isActive(ListTypes.Ordered);

  let isDisabled;
  if (isInList) {
    isDisabled = !editor.can().sinkListItem('listItem');
  } else {
    const currentIndent = getIndentLevel(editor);
    isDisabled = currentIndent >= maxLevel;
  }

  const handleClick = () => {
    if (isInList) {
      editor.chain().focus().sinkListItem('listItem').run();
    } else {
      editor.chain().focus().indent().run();
    }
  };

  return (
    <MenuButton
      disabled={isDisabled}
      onClick={handleClick}
      tooltipLabel="Increase indent"
      IconComponent={FormatIndentIncreaseIcon}
      {...props}
    />
  );
};

export const MenuButtonDecreaseIndent = (props: Partial<MenuButtonProps>) => {
  const editor = useRichTextEditorContext();

  if (!editor) {
    return null;
  }

  const isInList =
    editor.isActive(ListTypes.Bullet) || editor.isActive(ListTypes.Ordered);

  let isDisabled;
  if (isInList) {
    isDisabled = !editor.can().liftListItem('listItem');
  } else {
    const currentIndent = getIndentLevel(editor);
    isDisabled = currentIndent === 0;
  }

  const handleClick = () => {
    if (isInList) {
      editor.chain().focus().liftListItem('listItem').run();
    } else {
      editor.chain().focus().outdent().run();
    }
  };

  return (
    <MenuButton
      disabled={isDisabled}
      onClick={handleClick}
      tooltipLabel="Decrease indent"
      IconComponent={FormatIndentDecreaseIcon}
      {...props}
    />
  );
};
